<template>
  <div class="file-loader-table">
    <div class="file-loader-table__content">
      <data-table
        :headers="headers"
        :items="filteredFiles"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :item-class="getRowClass"
        class="elevation-0"
        :aria-label="'File loader jobs table'"
        :data-testid="'file-loader-table'"
        @click:row="handleRowClick"
      >
      <!-- Loading slot -->
      <template #progress>
        <v-progress-linear
          indeterminate
          color="primary"
          aria-live="polite"
          aria-label="Loading file loader data"
        />
      </template>

      <!-- Date/Time column -->
      <template #item.loadedDate="{ item }">
        <span class="text-body-2 table-cell-text">
          {{ formatDate(item.loadedDate, 'full') }}
        </span>
      </template>

      <!-- File Name column (stacked with file type) -->
      <template #item.fileName="{ item }">
        <table-cell-stacked
          :primary="extractFileName(item.fileName)"
          :secondary="item.fileType"
        />
      </template>

      <!-- Status column -->
      <template #item.loadStatus="{ item }">
        <div class="d-flex align-center justify-center">
          <status-badge
            :status="getDerivedStatus(item).label"
            :type="getDerivedStatus(item).type"
            :aria-label="`Status: ${getDerivedStatus(item).label}`"
          />
        </div>
      </template>

      <!-- Rows Accepted column -->
      <template #item.rowsAccepted="{ item }">
        <div class="d-flex align-center justify-center">
          <span
            class="text-body-2 font-weight-medium"
            :class="item.rowsAccepted > 0 ? 'success--text' : 'table-cell-text'"
          >
            {{ item.rowsAccepted.toLocaleString() }}
          </span>
        </div>
      </template>

      <!-- Rows Rejected column -->
      <template #item.rowsRejected="{ item }">
        <div class="d-flex align-center justify-center">
          <span
            class="text-body-2 font-weight-medium"
            :class="item.rowsRejected > 0 ? 'error--text' : 'table-cell-text'"
          >
            {{ item.rowsRejected.toLocaleString() }}
          </span>
        </div>
      </template>

      <!-- Submissions column -->
      <template #item.submissionsCreated="{ item }">
        <div class="d-flex align-center justify-center">
          <span class="text-body-2 table-cell-text">
            {{ item.submissionsCreated.toLocaleString() }}
          </span>
        </div>
      </template>

      <!-- Column header tooltips -->
      <template #header.submissionsCreated="{ header }">
        <div class="d-flex align-center justify-center">
          <span>{{ header.text }}</span>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                x-small
                class="ml-1"
                color="grey"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>
              Where multiple rows relate to the same individual, they are grouped into a single submission.
              This figure may differ from the total Rows Accepted.
            </span>
          </v-tooltip>
        </div>
      </template>
      <template #header.durationSeconds="{ header }">
        <div class="d-flex align-center justify-center">
          <span>{{ header.text }}</span>
          <v-tooltip left max-width="380">
            <template #activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                x-small
                class="ml-1"
                color="grey"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Time taken to load the file into the system only. This does not include downstream data processing time.</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Duration column -->
      <template #item.durationSeconds="{ item }">
        <div class="d-flex align-center justify-center">
          <span class="text-body-2 table-cell-text">
            {{ formatDuration(item.durationSeconds) }}
          </span>
        </div>
      </template>

      <!-- Empty state -->
      <template #no-data>
        <div class="text-center py-8">
          <v-icon size="48" color="grey lighten-1">mdi-file-upload-outline</v-icon>
          <div class="text-h6 grey--text mt-4">No file loader jobs found</div>
          <div class="text-body-2 grey--text">File loader jobs will appear here once files are uploaded</div>
        </div>
      </template>
      </data-table>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import DataTable from '../DataTable/DataTable.vue'
import StatusBadge from '../StatusBadge/StatusBadge.vue'
import TableCellStacked from '../DataTable/cells/TableCellStacked.vue'
import type { FileLoadJob } from './types'
import { jobFailed, allRowsRejected } from './fileLoadStatus'
import { formatDate, formatDuration } from '../../utils/formatters'

export default Vue.extend({
  name: 'FileLoaderTableView',

  components: {
    DataTable,
    StatusBadge,
    TableCellStacked
  },

  props: {
    /** Array of file load jobs */
    files: {
      type: Array as PropType<FileLoadJob[]>,
      required: true
    },
    /** Loading state */
    loading: {
      type: Boolean,
      default: false
    },
    /** Items per page */
    itemsPerPage: {
      type: Number,
      default: 15
    }
  },

  data() {
    return {
      headers: [
        {
          text: 'Date/Time',
          value: 'loadedDate',
          sortable: false,
          align: 'left' as const,
          width: '12%'
        },
        {
          text: 'File name',
          value: 'fileName',
          sortable: false,
          align: 'left' as const,
          width: '28%'
        },
        {
          text: 'Status',
          value: 'loadStatus',
          sortable: false,
          align: 'center' as const,
          width: '18%'
        },
        {
          text: 'Rows Accepted',
          value: 'rowsAccepted',
          sortable: false,
          width: '10%',
          align: 'center' as const
        },
        {
          text: 'Rows Rejected',
          value: 'rowsRejected',
          sortable: false,
          width: '10%',
          align: 'center' as const
        },
        {
          text: 'Submissions Created',
          value: 'submissionsCreated',
          sortable: false,
          width: '12%',
          align: 'center' as const
        },
        {
          text: 'Duration',
          value: 'durationSeconds',
          sortable: false,
          width: '10%',
          align: 'center' as const
        }
      ]
    }
  },

  computed: {
    filteredFiles(): FileLoadJob[] {
      return this.files
    }
  },

  methods: {
    formatDate,
    formatDuration,

    /** Extract just the filename from a full path */
    extractFileName(fullPath: string): string {
      if (!fullPath) return ''
      // Handle both Windows (\) and Unix (/) path separators
      const parts = fullPath.split(/[\\/]/)
      return parts[parts.length - 1] || fullPath
    },

    /** Return CSS class for row - all rows are clickable to open the details drawer */
    getRowClass(_item: FileLoadJob): string {
      return 'clickable-row'
    },

    /** Derive a 4-way status from the raw loadStatus and row counts */
    getDerivedStatus(item: FileLoadJob): { label: string; type: string } {
      if (jobFailed(item)) {
        return { label: 'Processing Failed', type: 'error' }
      }
      if (item.rowsRejected > 0 && item.rowsAccepted > 0) {
        return { label: 'Processed - Partially Accepted', type: 'warning' }
      }
      if (allRowsRejected(item)) {
        return { label: 'Processed - All Rejected', type: 'error' }
      }
      return { label: 'Processed - All Accepted', type: 'success' }
    },

    /** Handle row click - emit view-details event to open the details drawer */
    handleRowClick(item: FileLoadJob) {
      this.$emit('view-details', item.fileLoaderGuid)
    }
  }
})
</script>

<style scoped>
.file-loader-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: var(--global-card-background, white);
  border-radius: var(--radius);
  overflow: hidden;
  min-height: 0; /* Allow flex shrinking */
  width: 100%;
  margin-bottom: 0 !important;
}

.file-loader-table__content {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
}

.file-loader-table >>> .v-data-table {
  border: none;
  border-radius: 0;
}

/* Header styling - aligned with shared data-table */
.file-loader-table >>> .v-data-table thead th {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.75rem; /* 12px - aligned with shared data-table */
  color: var(--global-table-header-text, var(--global-muted-foreground));
  padding: 12px 16px !important;
  height: auto !important;
  vertical-align: middle !important;
  background-color: var(--global-card-background, white) !important;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.12)) !important;
}

/* Header text alignment */
.file-loader-table >>> .v-data-table thead th span {
  display: block;
}

/* Disable pointer cursor on header - columns not sortable */
.file-loader-table >>> .v-data-table thead th {
  cursor: default !important;
}

/* Row base state */
.file-loader-table >>> .v-data-table tbody tr {
  cursor: default;
  transition: background-color 0.2s ease;
}

.file-loader-table >>> .v-data-table tbody tr:hover {
  background-color: var(--global-table-row-hover, #f5f5f5);
}

/* Pointer cursor only on rows with rejections */
.file-loader-table >>> .v-data-table tbody tr.clickable-row {
  cursor: pointer;
}

/* Cell styling - aligned with shared data-table */
.file-loader-table >>> .v-data-table tbody td {
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem; /* 14px - aligned with shared data-table */
  font-weight: 500;
  color: var(--global-foreground);
  padding: 12px 16px !important;
  height: auto !important;
  vertical-align: middle !important;
  border-bottom: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.06)) !important;
}

.file-loader-table >>> .v-data-table tbody tr {
  height: auto;
}

/* Pagination Footer */
.file-loader-table__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 4px;
  border-top: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.12));
  flex-shrink: 0;
}

.file-loader-table__footer-info {
  display: flex;
  align-items: center;
}

.file-loader-table__footer-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.file-loader-table__page-indicator {
  margin: 0 12px;
  white-space: nowrap;
}

.gap-2 {
  gap: 8px;
}

/* Table cell text color */
.table-cell-text {
  color: var(--global-foreground, #374151) !important;
}

/* Enforce percentage column widths so long filenames wrap instead of expanding the table */
.file-loader-table >>> .v-data-table table {
  table-layout: fixed;
  width: 100%;
}
</style>
